/**
 * SourcePreviewDrawer — shows matched clause text with highlighting.
 * Opens as an overlay when user clicks "Locate" on a clause review flag.
 * Displays the evidence quote in context with surrounding text.
 */
import { useEffect, useRef } from 'react';
import { APP } from '../tokens';
import type { ClauseFlagLocateResult } from '@corner/shared';

const F = APP.fontFamily;

interface SourcePreviewDrawerProps {
  result: ClauseFlagLocateResult;
  sectionRef: string;
  sectionTitle: string;
  onClose: () => void;
  /** Whether locate call is still loading */
  loading?: boolean;
}

export default function SourcePreviewDrawer({
  result,
  sectionRef,
  sectionTitle,
  onClose,
  loading,
}: SourcePreviewDrawerProps) {
  const highlightRef = useRef<HTMLSpanElement>(null);

  // Auto-scroll to the highlight after render
  useEffect(() => {
    if (result.matched && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [result.matched]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.25)',
        }}
      />

      {/* Drawer panel */}
      <div
        style={{
          position: 'relative',
          width: 480,
          maxWidth: '90vw',
          height: '100vh',
          backgroundColor: '#FDFCFB',
          borderLeft: '1px solid #E8DED4',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid #E8DED4',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: '#D97645',
                fontFamily: F,
              }}
            >
              Source Preview
            </span>
            <span style={{ fontSize: 12, fontWeight: 500, color: APP.textDark, fontFamily: F }}>
              §{sectionRef} {sectionTitle}
              {result.page != null && (
                <span style={{ fontWeight: 400, color: '#9E9189' }}> · Page {result.page}</span>
              )}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: 6,
              backgroundColor: 'transparent',
              border: '1px solid #E8DED4',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#6B5E52" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Match status indicator */}
        <div
          style={{
            padding: '8px 16px',
            borderBottom: '1px solid #E8DED4',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {loading ? (
            <span style={{ fontSize: 11, color: '#9E9189', fontFamily: F }}>
              Searching document...
            </span>
          ) : result.matched ? (
            <>
              <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#4A9E6B', flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: '#4A9E6B', fontWeight: 500, fontFamily: F }}>
                Exact match found
              </span>
            </>
          ) : (
            <>
              <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#D4A843', flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: '#B5930E', fontWeight: 500, fontFamily: F }}>
                Approximate location — use Ctrl+F for precise match
              </span>
            </>
          )}
        </div>

        {/* Content */}
        <div
          style={{
            flexGrow: 1,
            overflow: 'auto',
            padding: '16px',
          }}
        >
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120 }}>
              <span style={{ fontSize: 12, color: '#9E9189', fontFamily: F }}>Extracting document text...</span>
            </div>
          ) : result.matched ? (
            /* Matched view: context window with highlighted snippet */
            <div
              style={{
                fontFamily: '"Georgia", serif',
                fontSize: 13,
                lineHeight: '22px',
                color: '#4A3F35',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {result.before && (
                <span style={{ color: '#9E9189' }}>
                  {result.before.startsWith(' ') || result.before.startsWith('\n') ? '' : '...'}
                  {result.before}
                </span>
              )}
              <span
                ref={highlightRef}
                style={{
                  backgroundColor: 'rgba(255, 213, 79, 0.35)',
                  borderRadius: 2,
                  padding: '1px 0',
                  boxShadow: '0 0 0 2px rgba(255, 213, 79, 0.2)',
                }}
              >
                {result.snippet}
              </span>
              {result.after && (
                <span style={{ color: '#9E9189' }}>
                  {result.after}
                  {result.after.endsWith(' ') || result.after.endsWith('\n') ? '' : '...'}
                </span>
              )}
            </div>
          ) : result.pageText ? (
            /* No match but have page text: show full page with evidence quote call-out */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div
                style={{
                  padding: '8px 10px',
                  borderRadius: 6,
                  backgroundColor: '#FFF8E7',
                  border: '1px solid #F0E3C0',
                }}
              >
                <span style={{ fontSize: 11, fontWeight: 500, color: '#B5930E', fontFamily: F, display: 'block', marginBottom: 4 }}>
                  Evidence quote (not precisely located):
                </span>
                <span style={{ fontSize: 12, color: '#6B5E52', fontFamily: '"Georgia", serif', lineHeight: '18px' }}>
                  "{result.snippet}"
                </span>
              </div>
              <div
                style={{
                  fontFamily: '"Georgia", serif',
                  fontSize: 12,
                  lineHeight: '20px',
                  color: '#6B5E52',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {result.pageText}
              </div>
            </div>
          ) : (
            /* No match, no page text: show the evidence quote alone */
            <div
              style={{
                padding: '12px 14px',
                borderRadius: 6,
                backgroundColor: '#FFF8E7',
                border: '1px solid #F0E3C0',
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 500, color: '#B5930E', fontFamily: F, display: 'block', marginBottom: 6 }}>
                Evidence quote from analysis:
              </span>
              <span
                style={{
                  fontSize: 13,
                  color: '#4A3F35',
                  fontFamily: '"Georgia", serif',
                  lineHeight: '20px',
                  display: 'block',
                }}
              >
                "{result.snippet}"
              </span>
              <span style={{ fontSize: 11, color: '#9E9189', fontFamily: F, display: 'block', marginTop: 8 }}>
                This text could not be precisely located in the document.
                {result.page != null
                  ? ` The document viewer has been navigated to page ${result.page}.`
                  : ' Try using Ctrl+F (Cmd+F) with the copied text.'}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '10px 16px',
            borderTop: '1px solid #E8DED4',
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(result.snippet).catch(() => {});
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '5px 12px',
              borderRadius: 5,
              backgroundColor: '#FDFCFB',
              border: '1px solid #E8DED4',
              cursor: 'pointer',
              fontFamily: F,
              fontSize: 11,
              fontWeight: 500,
              color: '#6B5E52',
            }}
          >
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="#6B5E52" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
            </svg>
            Copy excerpt
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '5px 12px',
              borderRadius: 5,
              backgroundColor: APP.textDark,
              border: 'none',
              cursor: 'pointer',
              fontFamily: F,
              fontSize: 11,
              fontWeight: 500,
              color: '#FDFCFB',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
